import { LANGUAGE_CONFIG } from "../../config";

/**
 * Strips a trailing slash from a pathname, leaving `/` unchanged.
 *
 * @param {string} pathname Path to normalize.
 * @returns {string} Path without a trailing slash.
 */
const stripTrailingSlash = (pathname) => {
  if (!pathname || pathname === "/") {
    return pathname || "/";
  }
  return pathname.replace(/\/$/, "");
};

/**
 * Returns the backend origin used by public auth forms.
 *
 * @returns {string} Backend origin without a trailing slash.
 */
export const getAuthBackendOrigin = () => {
  if (window.location.port === "3000") {
    return `${window.location.protocol}//${window.location.hostname}:5000`;
  }

  return window.location.origin;
};

/**
 * Returns a React-app next URL suitable for backend redirects after login.
 *
 * @param {string|null} nextParam Next value from the login page query string.
 * @returns {string} Redirect target for successful login.
 */
export const getLoginNextUrl = (nextParam) => {
  if (nextParam?.startsWith("http")) {
    const nextUrl = new URL(nextParam);
    if (nextUrl.pathname === "/") {
      return `${nextUrl.origin}/app/home`;
    }
    return nextParam;
  }

  const nextPath = nextParam && nextParam !== "/" ? nextParam : "/app/home";
  return `${window.location.origin}${nextPath}`;
};

/**
 * Resolves login-page language from a pathname (router or window).
 * `/login/es` and `/app/login/es` are Spanish; `/login/en` and `/app/login/en`
 * are English. Bare `/login` defaults to English without persisting.
 *
 * @param {string} [pathname=window.location.pathname] Path including or excluding `/app`.
 * @returns {string} `en` or `es`.
 */
export const getLoginPathLanguage = (pathname = window.location.pathname) => {
  const normalized = stripTrailingSlash(pathname);
  if (normalized.endsWith("/login/es")) {
    return LANGUAGE_CONFIG.SPANISH;
  }
  return LANGUAGE_CONFIG.ENGLISH;
};

/**
 * Language to write on successful login, or `null` when the path must not persist.
 *
 * @param {string} [pathname=window.location.pathname] Path including or excluding `/app`.
 * @returns {string|null} `en`, `es`, or `null` for bare `/login`.
 */
export const getPersistableLoginLanguage = (
  pathname = window.location.pathname
) => {
  const normalized = stripTrailingSlash(pathname);
  if (normalized.endsWith("/login/es")) {
    return LANGUAGE_CONFIG.SPANISH;
  }
  if (normalized.endsWith("/login/en")) {
    return LANGUAGE_CONFIG.ENGLISH;
  }
  return null;
};

/**
 * React-router login path for a language, keeping the current query string.
 * Always uses `/login/es` or `/login/en` so a manual toggle also persists.
 *
 * @param {string} language Language code (`en` or `es`).
 * @param {string} [search=""] Query string including `?`.
 * @returns {string} Path under the `/app` basename.
 */
export const getLoginRouteForLanguage = (language, search = "") => {
  const suffix =
    language === LANGUAGE_CONFIG.SPANISH
      ? `/${LANGUAGE_CONFIG.SPANISH}`
      : `/${LANGUAGE_CONFIG.ENGLISH}`;
  return `/login${suffix}${search || ""}`;
};

/**
 * Whether a router or window path is a language-force override.
 *
 * @param {string} [pathname=window.location.pathname] Path with or without `/app`.
 * @returns {boolean} True for `/es`, `/en`, `/app/es`, and `/app/en`.
 */
export const isLanguageForcePath = (pathname = window.location.pathname) => {
  const normalized = stripTrailingSlash(pathname);
  return (
    normalized === `/${LANGUAGE_CONFIG.SPANISH}` ||
    normalized === `/${LANGUAGE_CONFIG.ENGLISH}` ||
    normalized === `/app/${LANGUAGE_CONFIG.SPANISH}` ||
    normalized === `/app/${LANGUAGE_CONFIG.ENGLISH}`
  );
};

/**
 * Absolute login URL for a language-force guest redirect.
 *
 * @param {string} language Language code (`en` or `es`).
 * @returns {string} Same-origin `/app/login/en` or `/app/login/es`.
 */
export const getLanguageForceLoginHref = (language) =>
  `${window.location.origin}/app${getLoginRouteForLanguage(language)}`;

/**
 * Absolute site-home URL after a successful language-force write.
 *
 * @returns {string} Django BASE_URL `/` (localhost uses the :5000 origin).
 */
export const getLanguageForceHomeHref = () => `${getAuthBackendOrigin()}/`;

/**
 * Login route for an unauthenticated visitor, matching language-force suffixes.
 *
 * @param {string} currentPath Full window path plus search (`/app/es`).
 * @returns {string} Basename-relative login path with `next`.
 */
export const getGuestLoginRoute = (currentPath) => {
  const pathname = stripTrailingSlash(currentPath.split("?")[0] || "/");
  const next = encodeURIComponent(currentPath);
  if (pathname === "/app/es" || pathname === "/es") {
    return `/login/es?next=${next}`;
  }
  if (pathname === "/app/en" || pathname === "/en") {
    return `/login/en?next=${next}`;
  }
  return `/login?next=${next}`;
};
